import { Component, type ReactNode } from 'react';
import { Modal, Button, Typography, Space, Result } from 'antd';
import { ReloadOutlined, WarningOutlined } from '@ant-design/icons';
import { FormDirtyContext } from '../../context/FormDirtyContext';

const { Text, Paragraph } = Typography;

interface ChunkErrorBoundaryProps {
  children: ReactNode;
}

interface ChunkErrorBoundaryState {
  hasError: boolean;
  isChunkError: boolean;
  showUpdateModal: boolean;
  errorMessage: string | null;
}

/**
 * Error boundary that handles chunk load failures after deployments.
 *
 * When a new version is deployed and old chunks are replaced, dynamic imports
 * fail with ChunkLoadError. This boundary catches those errors and:
 * - Auto-refreshes if no forms have unsaved changes
 * - Prompts the operator if there are unsaved changes
 *
 * @see FormDirtyContext for tracking unsaved form state
 */
export class ChunkErrorBoundary extends Component<ChunkErrorBoundaryProps, ChunkErrorBoundaryState> {
  static contextType = FormDirtyContext;
  declare context: React.ContextType<typeof FormDirtyContext>;

  constructor(props: ChunkErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      isChunkError: false,
      showUpdateModal: false,
      errorMessage: null
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ChunkErrorBoundaryState> {
    const isChunkError = ChunkErrorBoundary.isChunkLoadError(error);
    // Capture the full error message including toString() for better debugging
    const errorMessage = error.message || error.toString?.() || 'Unknown error';
    
    console.log('[ChunkErrorBoundary] getDerivedStateFromError:', {
      message: error.message,
      name: error.name,
      isChunkError,
      errorMessage
    });
    
    return {
      hasError: true,
      isChunkError,
      errorMessage
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    const isChunkError = ChunkErrorBoundary.isChunkLoadError(error);
    
    // Log the error for debugging/monitoring
    console.error('[ChunkErrorBoundary] Caught error:', {
      message: error.message,
      name: error.name,
      toString: error.toString?.(),
      cause: (error as Error & { cause?: Error }).cause,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      isChunkError
    });

    // Force update state if detection differs from initial getDerivedStateFromError
    // This handles edge cases where the error format might be parsed differently
    if (isChunkError && !this.state.isChunkError) {
      console.warn('[ChunkErrorBoundary] Re-detected as chunk error, updating state');
      this.setState({ isChunkError: true }, () => {
        this.handleChunkError();
      });
    } else if (isChunkError) {
      this.handleChunkError();
    }
  }

  /**
   * Detect chunk load errors from various bundlers (Vite, Webpack, Rspack).
   * These errors occur when dynamic imports fail to load chunks that no longer exist.
   */
  static isChunkLoadError(error: Error): boolean {
    // Check the error and any nested cause
    const messagesToCheck: string[] = [];
    const namesToCheck: string[] = [];

    // Collect messages and names from error chain
    let currentError: Error | undefined = error;
    while (currentError) {
      if (currentError.message) {
        messagesToCheck.push(currentError.message.toLowerCase());
      }
      if (currentError.name) {
        namesToCheck.push(currentError.name.toLowerCase());
      }
      // Check for nested cause (ES2022 Error cause)
      currentError = (currentError as Error & { cause?: Error }).cause;
    }

    // Also check error.toString() in case message is formatted differently
    const errorString = error.toString?.()?.toLowerCase() || '';
    if (errorString && !messagesToCheck.includes(errorString)) {
      messagesToCheck.push(errorString);
    }

    // Check all collected messages and names
    for (const name of namesToCheck) {
      if (name === 'chunkloaderror') {
        return true;
      }
    }

    for (const message of messagesToCheck) {
      if (
        // Webpack/Rspack chunk errors
        message.includes('loading chunk') ||
        message.includes('loading css chunk') ||
        // Rspack async chunk errors
        message.includes('.async.js') ||
        message.includes('.async.css') ||
        // Vite dynamic import errors
        message.includes('failed to fetch dynamically imported module') ||
        message.includes('dynamically imported module') ||
        // Vite CSS preload errors (MO-4225)
        // e.g., "Unable to preload CSS for /assets/details-CDURMc43.css"
        message.includes('unable to preload css') ||
        // Generic fetch failures for JS/CSS files
        (message.includes('failed to fetch') && (message.includes('.js') || message.includes('.css'))) ||
        // Network errors during chunk load
        message.includes('error loading dynamically imported module') ||
        // Safari/WebKit dynamic import failures (UTXS-840)
        message.includes('importing a module script failed') ||
        // Rspack specific patterns
        message.includes('chunk load failed') ||
        (message.includes('unexpected token') && message.includes('.js'))
      ) {
        return true;
      }
    }

    return false;
  }

  /**
   * Handle chunk load error based on form dirty state.
   * Auto-refresh if clean, show modal if dirty.
   */
  private handleChunkError(): void {
    const { isDirty } = this.context;

    if (!isDirty) {
      // No unsaved work - auto-refresh with brief delay for toast visibility
      this.autoRefresh();
    } else {
      // Has unsaved work - show prompt modal
      this.setState({ showUpdateModal: true });
    }
  }

  /**
   * Perform page refresh to load the latest version.
   * Includes protection against infinite reload loops.
   */
  private autoRefresh = (): void => {
    // Check for infinite reload loops
    const reloadCountKey = 'chunk_error_reload_count';
    const reloadTimestampKey = 'chunk_error_last_reload';
    
    const now = Date.now();
    const lastReloadStr = sessionStorage.getItem(reloadTimestampKey);
    let count = parseInt(sessionStorage.getItem(reloadCountKey) || '0', 10);
    
    if (lastReloadStr) {
      const lastReload = parseInt(lastReloadStr, 10);
      // If the last reload was more than 30 seconds ago, reset count
      if (!isNaN(lastReload) && now - lastReload > 30000) {
        count = 0;
      }
    }
    
    if (count >= 2) {
      console.error('[ChunkErrorBoundary] Prevented infinite reload loop');
      this.setState({ 
        hasError: true, 
        isChunkError: false, 
        errorMessage: 'Failed to load application modules. The server may be experiencing issues or you may be rate-limited.' 
      });
      return;
    }
    
    sessionStorage.setItem(reloadCountKey, (count + 1).toString());
    sessionStorage.setItem(reloadTimestampKey, now.toString());

    // Clear any cached chunks by adding timestamp to URL
    const url = new URL(window.location.href);
    url.searchParams.set('_reload', now.toString());

    // Use replace to avoid polluting browser history
    window.location.replace(url.toString());
  };

  /**
   * Handle user clicking "Reload Now" in the modal.
   */
  private handleReloadClick = (): void => {
    this.setState({ showUpdateModal: false });
    this.autoRefresh();
  };

  /**
   * Handle user dismissing the modal (continue working).
   * Note: The app may be in a broken state, so we show a warning.
   */
  private handleContinueClick = (): void => {
    this.setState({ showUpdateModal: false });
  };

  /**
   * Reset error state and retry (for non-chunk errors).
   */
  private handleRetry = (): void => {
    this.setState({
      hasError: false,
      isChunkError: false,
      showUpdateModal: false,
      errorMessage: null
    });
  };

  render(): ReactNode {
    const { hasError, isChunkError, showUpdateModal, errorMessage } = this.state;
    const { children } = this.props;

    // Show update modal for chunk errors with unsaved changes
    if (showUpdateModal) {
      return (
        <>
          {children}
          <Modal
            open={showUpdateModal}
            title={
              <Space>
                <WarningOutlined style={{ color: '#faad14' }} />
                <span>New Version Available</span>
              </Space>
            }
            closable={false}
            maskClosable={false}
            footer={[
              <Button key="continue" onClick={this.handleContinueClick}>
                Continue Working
              </Button>,
              <Button key="reload" type="primary" icon={<ReloadOutlined />} onClick={this.handleReloadClick}>
                Reload Now
              </Button>
            ]}>
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <Paragraph>
                A new version of the application has been deployed. To continue, the page needs to reload.
              </Paragraph>
              <Paragraph type="warning">
                <Text strong>You have unsaved changes.</Text> If you reload now, your unsaved work will be lost.
              </Paragraph>
              <Paragraph type="secondary">
                You can click "Continue Working" to try to save your changes first, but some features may not work until
                you reload.
              </Paragraph>
            </Space>
          </Modal>
        </>
      );
    }

    // For chunk errors without unsaved changes, we auto-refresh (handled in componentDidCatch)
    // Show a loading state while refreshing
    if (hasError && isChunkError) {
      return (
        <Result
          icon={<ReloadOutlined spin style={{ fontSize: 48, color: '#1890ff' }} />}
          title="Updating to latest version..."
          subTitle="The page will refresh automatically."
        />
      );
    }

    // For non-chunk errors, show a generic error page with retry option
    if (hasError && !isChunkError) {
      return (
        <Result
          status="error"
          title="Something went wrong"
          subTitle={
            <Space direction="vertical">
              <Text>An unexpected error occurred. Please try again.</Text>
              {errorMessage && (
                <Text type="secondary" code style={{ fontSize: 12 }}>
                  {errorMessage}
                </Text>
              )}
            </Space>
          }
          extra={[
            <Button key="retry" onClick={this.handleRetry}>
              Try Again
            </Button>,
            <Button key="reload" type="primary" icon={<ReloadOutlined />} onClick={this.autoRefresh}>
              Reload Page
            </Button>
          ]}
        />
      );
    }

    return children;
  }
}

export default ChunkErrorBoundary;
